/**
 * Normalise un libellé de type de voie (long, capitalisation libre)
 * vers le **code court** attendu par les démarches ANTS/MiSIV et par
 * les `<Select>` du formulaire CG (cf. SubmitCTVO.tsx ligne 1361+).
 *
 * Cas d'usage : l'OCR justif_domicile / piece_identite remonte des
 * libellés longs comme "BOULEVARD", "AV." ou "Bd" ; le backend stocke
 * et le Select downstream attend le code "blvd". Sans normalisation,
 * le Select reste vide et le relecteur croit que le champ n'est pas
 * extrait alors qu'il l'est, juste dans un format incompatible.
 *
 * Mapping aligné sur les valeurs du `<Select>` dans
 * `DealStageActions/SubmitCTVO.tsx` (codes officiels ANTS).
 *
 * Tolérant à : capitalisation, points, abréviations courantes (BD, AV…).
 * Une valeur LUE mais hors nomenclature MiSIV retourne `'sans'` (« Inconnu »),
 * jamais une conversion approchante — cf. l'arbitrage cité plus bas. Une valeur
 * absente retourne `''` : rien n'a été lu, il n'y a rien à conclure.
 */

const TYPE_VOIE_BY_FULL_LABEL: Record<string, string> = {
  // Code → libellés acceptés (uppercased, sans ponctuation)
  rue: ['RUE', 'R'].join('|'),
  all: ['ALLEE', 'ALLÉE', 'ALL'].join('|'),
  avn: ['AVENUE', 'AV', 'AVE'].join('|'),
  blvd: ['BOULEVARD', 'BD', 'BLVD', 'BOUL'].join('|'),
  chau: ['CHAUSSEE', 'CHAUSSÉE', 'CHS'].join('|'),
  che: ['CHEMIN', 'CH', 'CHE', 'CHM'].join('|'),
  cour: ['COUR', 'COURS'].join('|'),
  fbg: ['FAUBOURG', 'FBG', 'FAUB'].join('|'),
  fort: ['FORT'].join('|'),
  imp: ['IMPASSE', 'IMP'].join('|'),
  ldit: ['LIEU-DIT', 'LIEUDIT', 'LIEU DIT', 'LD'].join('|'),
  parc: ['PARC'].join('|'),
  pass: ['PASSAGE', 'PAS', 'PSGE'].join('|'),
  plc: ['PLACE', 'PL'].join('|'),
  quai: ['QUAI'].join('|'),
  rte: ['ROUTE', 'RTE'].join('|'),
  sent: ['SENTIER', 'SENT'].join('|'),
  squa: ['SQUARE', 'SQ'].join('|'),
  sans: ['INCONNU', 'AUTRE'].join('|'),
};

/** Code MiSIV du type de voie « Inconnu » (cf. addressTypes.ts). */
const UNKNOWN_TYPE_VOIE_CODE = 'sans';

const LABEL_TO_CODE: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  Object.entries(TYPE_VOIE_BY_FULL_LABEL).forEach(([code, labels]) => {
    labels.split('|').forEach((label) => {
      m[label] = code;
    });
  });
  return m;
})();

const ALL_CODES: ReadonlySet<string> = new Set(Object.keys(TYPE_VOIE_BY_FULL_LABEL));

export const normalizeTypeVoie = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';

  // Si la valeur est déjà un code court reconnu (lowercase), on la garde
  // — utile quand on lit la valeur déjà normalisée depuis la DB.
  const asCode = trimmed.toLowerCase();
  if (ALL_CODES.has(asCode)) return asCode;

  // Sinon on tente le mapping libellé long → code, en uppercased + sans
  // ponctuation pour matcher tolérantement.
  const upper = trimmed
    .toUpperCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Un libellé LU mais hors nomenclature MiSIV (« Raccourci », « Traverse »…)
  // vaut « Inconnu », jamais une conversion vers le type le plus proche :
  // arbitrage François `type_voie_hors_liste` du 09/09/2026 — « il faut mettre
  // inconnu impérativement et pas de conversion car il faut adresse exacte »
  // (FJ-780-PN, où « raccourci » avait été rendu « allée »). Laisser le champ
  // vide n'était pas une option non plus : MiSIV attend un type de voie
  // renseigné, et le Select restait muet sans que le relecteur sache pourquoi.
  return LABEL_TO_CODE[upper] ?? UNKNOWN_TYPE_VOIE_CODE;
};

/** Libellés qui veulent dire « Inconnu » : rien à reporter dans le nom de voie. */
const UNKNOWN_LABELS = new Set(['INCONNU', 'AUTRE', 'SANS']);

/**
 * Type de voie hors nomenclature (« Lotissement », « Domaine », « Raccourci »,
 * « Traverse »…) : le code devient « Inconnu » (`sans`) ET le mot lu est reporté
 * en tête du nom de voie, sinon il disparaît de l'adresse envoyée au SIV
 * (SKIP-455 : « LOTISSEMENT LES CHÊNES » devenait « Inconnu / LES CHÊNES »).
 * Pas de doublon si le nom de voie commence déjà par ce mot.
 */
export const resolveTypeAndNomVoie = (args: {
  typeVoieLu: string | null | undefined;
  nomVoie: string | null | undefined;
}): { typeVoie: string; nomVoie: string } => {
  const nomVoie = String(args.nomVoie ?? '').trim();
  const lu = String(args.typeVoieLu ?? '').trim();
  const typeVoie = normalizeTypeVoie(lu);
  if (typeVoie !== UNKNOWN_TYPE_VOIE_CODE || !lu) return { typeVoie, nomVoie };
  const mot = lu.replace(/\s+/g, ' ');
  if (UNKNOWN_LABELS.has(mot.toUpperCase())) return { typeVoie, nomVoie };
  if (nomVoie.toUpperCase().startsWith(mot.toUpperCase())) return { typeVoie, nomVoie };
  return { typeVoie, nomVoie: `${mot} ${nomVoie}`.trim() };
};
